import { Controller } from "@hotwired/stimulus"
import { getAllJSON, sendJSON, errorDetail } from "../api.js"

// Formularz dodawania leada ubezpieczeniowego (pion "ubezpieczenia").
//
// connect(): pobiera słowniki (województwa, podkategorie ubezpieczeniowe → buduje mapę slug→id).
// categoryChange(): przełącza widoczne sekcje pól; ukrywa sekcje obcych kategorii.
// voivodeshipChange(): kaskada — powiaty dla wybranego województwa.
// submit(): buduje payload snake_case z WIDOCZNYCH pól → POST do endpoint value → 201/400.
//
// Kluczowe kontrakty (seq-5):
// - name = firstName + surname (surname opcjonalne — łączone przed wysyłką).
// - placements: komunikacyjne → id z radio OC/OC-AC; pozostałe → id podkategorii Ogólne danej kat.
// - Pola ukrytych sekcji SĄ POMIJANE w payloadzie — serwer odrzuca pola spoza wybranego form_type.
// - form_type serwer wywodzi z kategorii wybranej podkategorii; klient go NIE wysyła.
// - Zero innerHTML — cały DOM przez createElement + textContent.

// Mapowania zakodowane na stałe na podstawie migracji 0015_seed_insurance_categories:
// wartość radio → slug podkategorii
const RADIO_TO_SUBCAT_SLUG = {
  oc: "komunikacyjne-oc",
  oc_ac: "komunikacyjne-ocac",
}
// wartość option kategorii → slug podkategorii Ogólne tej kategorii
const CATEGORY_TO_OGOLNE_SLUG = {
  zycie: "zycie-ogolne",
  majatkowe: "majatkowe-ogolne",
  "majatkowe-zycie": "majatkowe-zycie-ogolne",
}

export default class extends Controller {
  static targets = [
    "phone", "voivodeship", "county", "kind", "nipField", "nip", "email",
    "cityText", "acquisitionSource", "acquiredAt", "note", "pricePln",
    "consentChannel", "consentClauseVersion", "consentGrantedAt", "consentExpiresAt", "status",
    // seq-4 targets — firstName/surname replaces old name; placements built from category + radio
    "firstName", "surname",
    "categorySelect",
    "sectionCommunication", "sectionLife", "sectionProperty", "sectionPropertyLife", "sectionCommon",
    "subcategoryRadio",
    "registrationNumber", "dateSource", "vehicleMake", "vehicleModel", "engineCapacity", "vehicleYear",
    "hasInsurance", "propertyType",
    "postalCode", "age",
    "currentInsurer", "policyEndDate", "estimatedPremium", "hasClaimsHistory", "profileNotes",
  ]

  static values = {
    endpoint: String,
  }

  // Mapa slug podkategorii → id (wypełniana przez #loadSubcategories)
  #subcatBySlug = new Map()

  async connect() {
    this.kindChange()
    this.categoryChange() // initial: ukryj wszystkie sekcje kategorii
    await Promise.all([
      this.#loadVoivodeships(),
      this.#loadSubcategories(),
    ])
  }

  // NIP dotyczy tylko firm — pokaż i wymagaj przy kind=firma, ukryj przy osoba.
  kindChange() {
    const isCompany = this.kindTarget.value === "firma"
    if (this.hasNipFieldTarget) this.nipFieldTarget.hidden = !isCompany
    this.nipTarget.required = isCompany
    if (!isCompany) this.nipTarget.value = ""
  }

  // Przełącza widoczne sekcje pól wg wybranej kategorii.
  // Sekcja sectionCommon jest wspólna dla wszystkich kategorii — widoczna gdy cokolwiek wybrane.
  categoryChange() {
    const cat = this.hasCategorySelectTarget ? this.categorySelectTarget.value : ""

    const allSections = [
      this.hasSectionCommunicationTarget ? this.sectionCommunicationTarget : null,
      this.hasSectionLifeTarget ? this.sectionLifeTarget : null,
      this.hasSectionPropertyTarget ? this.sectionPropertyTarget : null,
      this.hasSectionPropertyLifeTarget ? this.sectionPropertyLifeTarget : null,
    ].filter(Boolean)

    for (const s of allSections) s.hidden = true

    if (this.hasSectionCommonTarget) {
      this.sectionCommonTarget.hidden = !cat
    }

    if (cat === "komunikacyjne" && this.hasSectionCommunicationTarget) {
      this.sectionCommunicationTarget.hidden = false
    } else if (cat === "zycie" && this.hasSectionLifeTarget) {
      this.sectionLifeTarget.hidden = false
    } else if (cat === "majatkowe" && this.hasSectionPropertyTarget) {
      this.sectionPropertyTarget.hidden = false
    } else if (cat === "majatkowe-zycie" && this.hasSectionPropertyLifeTarget) {
      this.sectionPropertyLifeTarget.hidden = false
    }
  }

  async voivodeshipChange() {
    const id = this.voivodeshipTarget.value
    // Wyczyść powiat
    this.countyTarget.textContent = ""
    const emptyOpt = document.createElement("option")
    emptyOpt.value = ""
    emptyOpt.textContent = "— wybierz powiat —"
    this.countyTarget.appendChild(emptyOpt)

    if (!id) {
      this.countyTarget.disabled = true
      return
    }

    let counties
    try {
      counties = await getAllJSON(`/api/counties/?voivodeship=${id}`)
    } catch {
      return
    }
    for (const c of counties) {
      const opt = document.createElement("option")
      opt.value = c.id
      opt.textContent = c.name
      this.countyTarget.appendChild(opt)
    }
    this.countyTarget.disabled = false
  }

  async submit(event) {
    // Bez tego natywne wysłanie formularza nawiguje stronę i przerywa fetch
    // (NS_BINDING_ABORTED) — lead się nie zapisuje, a toast nie zdąży się pojawić.
    event?.preventDefault()
    this.#clearStatus()

    // Buduj placements PRZED wywołaniem #buildPayload, żeby móc wcześnie przerwać
    // przy braku podkategorii w mapie slug→id (np. po zmianie nazwy sluga w migracji).
    // null = slug istnieje w stałej, ale nie ma go w odpowiedzi API → błąd konfiguracji,
    // NIE ten sam co "nie zaznaczono radia" ([] bez wybranego radia).
    const category = this.hasCategorySelectTarget ? this.categorySelectTarget.value : ""
    const placements = this.#buildPlacements(category)
    if (placements === null) {
      this.#showError("Błąd konfiguracji: podkategoria ubezpieczeniowa nie istnieje w bazie. Odśwież stronę lub skontaktuj się z administratorem.")
      return
    }

    const payload = this.#buildPayload(placements)
    const { ok, status, data } = await sendJSON("POST", this.endpointValue, payload)
    if (ok && status === 201) {
      this.#showSuccess("Lead został zapisany.")
      const form = this.element.tagName === "FORM"
        ? this.element
        : this.element.querySelector("form")
      // Po reset() wartości formularza wracają do stanu początkowego, ale pochodny
      // stan DOM (widoczność sekcji, wymagalność NIP, lista powiatów) musi zostać
      // re-wyprowadzony z nowych wartości — inaczej następny lead napotka ukryte
      // wymagane pole (NIP) lub widoczne sekcje bez wybranej kategorii (finding 2, 5).
      form?.reset()
      this.kindChange()
      this.categoryChange()
      await this.voivodeshipChange()
    } else {
      this.#showError(this.#extractError(data))
    }
  }

  // --- private ---

  async #loadVoivodeships() {
    let items
    try {
      items = await getAllJSON("/api/voivodeships/")
    } catch {
      return
    }
    for (const v of items) {
      const opt = document.createElement("option")
      opt.value = v.id
      opt.textContent = v.name
      this.voivodeshipTarget.appendChild(opt)
    }
  }

  // Pobiera podkategorie ubezpieczeniowe i buduje mapę slug→id używaną przy budowaniu placements.
  async #loadSubcategories() {
    let items
    try {
      items = await getAllJSON("/api/subcategories/?vertical=ubezpieczenia")
    } catch {
      return
    }
    this.#subcatBySlug.clear()
    for (const s of items) {
      this.#subcatBySlug.set(s.slug, s.id)
    }
  }

  // placements — wynik #buildPlacements — jest przekazywany jako parametr, żeby submit()
  // mógł sprawdzić wartość null (błąd konfiguracji) PRZED wysłaniem POST.
  #buildPayload(placements) {
    const str = (t) => {
      const val = t?.value?.trim()
      return val || undefined
    }
    const num = (t) => {
      const val = t?.value?.trim()
      return val ? Number(val) : undefined
    }

    const category = this.hasCategorySelectTarget ? this.categorySelectTarget.value : ""

    // name: firstName (wymagane) + surname (opcjonalne)
    const first = str(this.firstNameTarget) ?? ""
    const last = str(this.surnameTarget)
    const name = first ? (last ? `${first} ${last}` : first) : undefined

    // consent: obiekt zagnieżdżony tylko gdy którekolwiek pole jest wypełnione
    const cChannel = str(this.consentChannelTarget)
    const cVersion = str(this.consentClauseVersionTarget)
    const cGranted = str(this.consentGrantedAtTarget)
    const cExpires = str(this.consentExpiresAtTarget)
    const hasConsent = cChannel || cVersion || cGranted || cExpires
    const consent = hasConsent
      ? {
          ...(cChannel && { channel: cChannel }),
          ...(cVersion && { clause_version: cVersion }),
          ...(cGranted && { granted_at: cGranted }),
          ...(cExpires && { expires_at: cExpires }),
        }
      : undefined

    const payload = {}
    const phone = str(this.phoneTarget)
    if (phone !== undefined) payload.phone = phone
    const voivodeship = num(this.voivodeshipTarget)
    if (voivodeship !== undefined) payload.voivodeship = voivodeship
    const county = num(this.countyTarget)
    if (county !== undefined) payload.county = county
    const kind = str(this.kindTarget)
    if (kind !== undefined) payload.kind = kind
    if (name !== undefined) payload.name = name
    const nip = str(this.nipTarget)
    if (nip !== undefined) payload.nip = nip
    const email = str(this.emailTarget)
    if (email !== undefined) payload.email = email
    const cityText = str(this.cityTextTarget)
    if (cityText !== undefined) payload.city_text = cityText
    const acquisitionSource = str(this.acquisitionSourceTarget)
    if (acquisitionSource !== undefined) payload.acquisition_source = acquisitionSource
    const acquiredAt = str(this.acquiredAtTarget)
    if (acquiredAt !== undefined) payload.acquired_at = acquiredAt
    const note = str(this.noteTarget)
    if (note !== undefined) payload.note = note
    const pricePln = str(this.pricePlnTarget)
    if (pricePln !== undefined) payload.price_pln = pricePln
    if (placements.length) payload.placements = placements
    if (consent) payload.consent = consent

    // Pola kategorii komunikacyjnej — TYLKO gdy ta sekcja jest widoczna
    if (category === "komunikacyjne") {
      const regNum = str(this.registrationNumberTarget)
      if (regNum !== undefined) payload.registration_number = regNum
      const dateSource = str(this.dateSourceTarget)
      if (dateSource !== undefined) payload.date_source = dateSource
      const vehicleMake = str(this.vehicleMakeTarget)
      if (vehicleMake !== undefined) payload.vehicle_make = vehicleMake
      const vehicleModel = str(this.vehicleModelTarget)
      if (vehicleModel !== undefined) payload.vehicle_model = vehicleModel
      const engineCap = str(this.engineCapacityTarget)
      if (engineCap !== undefined) payload.engine_capacity = engineCap
      const vehicleYear = num(this.vehicleYearTarget)
      if (vehicleYear !== undefined) payload.vehicle_year = vehicleYear
    }

    // hasInsurance i propertyType — odczyt tylko z WIDOCZNEJ sekcji.
    // Każdy z tych targetów istnieje w kilku sekcjach (life/property/property_life);
    // serwer odrzuca pole spoza wybranego form_type, więc musimy wziąć je tylko z widocznej sekcji.
    const inVisibleSection = (el) => !el.closest("section[hidden]")

    const visibleHasInsurance = this.hasHasInsuranceTarget
      ? this.hasInsuranceTargets.find(inVisibleSection)
      : undefined
    if (visibleHasInsurance?.value) {
      payload.has_insurance = visibleHasInsurance.value === "true"
    }

    const visiblePropertyType = this.hasPropertyTypeTarget
      ? this.propertyTypeTargets.find(inVisibleSection)
      : undefined
    const propertyTypeVal = str(visiblePropertyType)
    if (propertyTypeVal !== undefined) payload.property_type = propertyTypeVal

    // sectionCommon — postalCode, age, currentInsurer, policyEndDate, estimatedPremium,
    // hasClaimsHistory, profileNotes — dołączane TYLKO gdy sekcja jest widoczna.
    if (this.hasSectionCommonTarget && !this.sectionCommonTarget.hidden) {
      const postalCode = str(this.postalCodeTarget)
      if (postalCode !== undefined) payload.postal_code = postalCode
      const age = num(this.ageTarget)
      if (age !== undefined) payload.age = age
      const currentInsurer = str(this.currentInsurerTarget)
      if (currentInsurer !== undefined) payload.current_insurer = currentInsurer
      const policyEndDate = str(this.policyEndDateTarget)
      if (policyEndDate !== undefined) payload.policy_end_date = policyEndDate
      const estimatedPremium = num(this.estimatedPremiumTarget)
      if (estimatedPremium !== undefined) payload.estimated_premium = estimatedPremium

      // hasClaimsHistory: select (value "true"/"false"/""） lub checkbox (legacy)
      const hchEl = this.hasClaimsHistoryTarget
      let hasClaimsHistory
      if (hchEl.type === "checkbox") {
        hasClaimsHistory = hchEl.checked
      } else if (hchEl.value !== "") {
        hasClaimsHistory = hchEl.value === "true" || hchEl.value === "1"
      }
      if (hasClaimsHistory !== undefined) payload.has_claims_history = hasClaimsHistory

      const profileNotes = str(this.profileNotesTarget)
      if (profileNotes !== undefined) payload.notes = profileNotes
    }

    return payload
  }

  // Buduje tablicę placements na podstawie wybranej kategorii.
  // Dla komunikacyjne: bierze id z mapy slug→id wg wybranego radia OC/OC-AC.
  // Dla pozostałych: bierze id podkategorii Ogólne danej kategorii.
  //
  // Zwracane wartości:
  //   [{subcategory: id}]  — sukces
  //   []                   — legitymna brak-wartości: brak kategorii, brak zaznaczonego radia
  //   null                 — slug znany w stałej mapy, ale NIEOBECNY w odpowiedzi API →
  //                          błąd konfiguracji (np. zmiana nazwy sluga po migracji 0015);
  //                          submit() musi przerwać z wyraźnym komunikatem, NIE wysyłać POST.
  #buildPlacements(category) {
    if (category === "komunikacyjne") {
      const checked = this.subcategoryRadioTargets.find((r) => r.checked)
      if (!checked) return []
      const subcatSlug = RADIO_TO_SUBCAT_SLUG[checked.value]
      // Nieznana wartość radia (nie oc ani oc_ac) — traktuj jak brak zaznaczenia
      if (!subcatSlug) return []
      const id = this.#subcatBySlug.get(subcatSlug)
      // subcatSlug pochodzi ze stałej, ale brak w mapie API → drift sluga w bazie
      if (id === undefined) return null
      return [{ subcategory: id }]
    }
    const ogolneSlug = CATEGORY_TO_OGOLNE_SLUG[category]
    // Brak kategorii lub nieznana kategoria → pusta tablica (nie błąd konfiguracji)
    if (!ogolneSlug) return []
    const id = this.#subcatBySlug.get(ogolneSlug)
    // ogolneSlug pochodzi ze stałej, ale brak w mapie API → drift sluga w bazie
    if (id === undefined) return null
    return [{ subcategory: id }]
  }

  #extractError(data) {
    if (!data) return "Wystąpił błąd. Spróbuj ponownie."
    const detail = errorDetail(data, null)
    if (detail) return detail
    // Błędy polowe DRF: {field: ["msg", ...], ...}
    const fieldErrors = Object.entries(data)
      .filter(([, v]) => Array.isArray(v))
      .flatMap(([k, msgs]) => msgs.map((m) => `${k}: ${m}`))
    return fieldErrors.length ? fieldErrors.join("; ") : "Wystąpił błąd. Spróbuj ponownie."
  }

  #clearStatus() {
    const s = this.statusTarget
    s.hidden = true
    s.textContent = ""
    s.classList.remove("ls-form-error", "ls-form-success")
  }

  #showError(message) {
    const s = this.statusTarget
    s.hidden = false
    s.classList.remove("ls-form-success")
    s.classList.add("ls-form-error")
    s.textContent = ""
    const span = document.createElement("span")
    span.textContent = message
    s.appendChild(span)
  }

  #showSuccess(message) {
    const s = this.statusTarget
    s.hidden = false
    s.classList.remove("ls-form-error")
    s.classList.add("ls-form-success")
    s.textContent = ""
    const span = document.createElement("span")
    span.textContent = message
    s.appendChild(span)
  }
}
